import axios from "axios";
import toast from "react-hot-toast";
import React from "react";
import { clearToastAction, setToastAction } from "../utilities/toastActions";
import {
  validateAudioFile,
  uploadAudioToS3,
  generateS3AudioUrl,
  getFileExtension,
  getAudioErrorInfo,
  logAudioError,
} from "../utils/audioUtils";
import UploadProgressToast from "../components/common/UploadProgressToast";

interface FileConfig {
  file: File;
  audioType: "narrative" | "interaction";
  speakers: { name: string; role: string }[];
  additionalContext: string;
  previousUpload?: {
    caseFileId: string;
    fileUrl: string;
    transcriptionUrl?: string;
    originalAudioType: "narrative" | "interaction";
    fileName: string;
    fileSize: string;
  };
  axonSource?: {
    evidenceId: string;
    title: string;
    size: number;
    duration: number;
    contentType: string;
  };
}

interface FormFillConfig {
  user?: any;
  templateId: string;
  onSuccess?: (submission: any) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: number) => void;
  onSubmissionCreated?: (submissionId: string) => void;
  // Where to take the officer if they click the "filling the form..." toast: for
  // a fill started away from the Forms page, that form's page, where the new
  // submission shows as processing.
  onViewProgress?: () => void;
  // Add to this already-filled submission instead of creating a new one. The
  // new audio fills what it can; nothing already on the form is cleared.
  submissionId?: string;
}

/**
 * Poll the form submission processing status until completion or error.
 */
const pollFormSubmissionStatus = async (
  submissionId: string,
  {
    onSuccess,
    onError,
    interval = 3000,
    maxAttempts = 200,
    toastId,
    supplement = false,
  }: {
    onSuccess?: (data: any) => void;
    onError?: (error: string) => void;
    interval?: number;
    maxAttempts?: number;
    toastId?: string;
    // Adding to a filled form: it ends "completed" either way, and a failure
    // comes back as supplement_error.
    supplement?: boolean;
  }
) => {
  let attempts = 0;

  const poll = async () => {
    try {
      const response = await axios.get(
        `/api/forms/submissions/${submissionId}/processing-status`
      );
      const { status, error, field_values, ai_confidence, filled_pdf_url, supplement_error, last_supplement } =
        response.data;

      if (status === "completed" && supplement && supplement_error) {
        if (toastId) toast.error(supplement_error, { id: toastId });
        onError?.(supplement_error);
        return;
      }

      if (status === "completed") {
        if (toastId) {
          const n = last_supplement?.updatedFieldIds?.length ?? 0;
          toast.success(
            supplement
              ? n
                ? `Added to the form: ${n} field${n === 1 ? "" : "s"} updated`
                : "Nothing new to add from that recording"
              : "Form filled successfully!",
            { id: toastId }
          );
        }
        onSuccess?.({
          status,
          field_values,
          ai_confidence,
          filled_pdf_url,
          last_supplement,
        });
        return;
      }

      if (status === "error") {
        const errorMsg = error || "Form filling failed";
        if (toastId) {
          toast.error(errorMsg, { id: toastId });
        }
        onError?.(errorMsg);
        return;
      }

      // Still processing
      attempts++;
      if (attempts >= maxAttempts) {
        const errorMsg = "Form filling timed out";
        if (toastId) {
          toast.error(errorMsg, { id: toastId });
        }
        onError?.(errorMsg);
        return;
      }

      setTimeout(poll, interval);
    } catch (error) {
      console.error("Error polling form submission status:", error);
      attempts++;
      if (attempts >= maxAttempts) {
        onError?.("Failed to check processing status");
        return;
      }
      setTimeout(poll, interval);
    }
  };

  poll();
};

/**
 * Process form fill with audio files.
 * Creates a submission, uploads audio files, triggers processing, and polls for completion.
 */
export const processFormFill = async (
  files: FileConfig[],
  config: FormFillConfig
) => {
  const { user, templateId, onSuccess, onError, onProgress, onSubmissionCreated } = config;
  const adding = !!config.submissionId;

  if (files.length === 0) {
    onError?.("No files to upload");
    return;
  }

  // Validate all files (skip previous uploads & Axon — already validated)
  for (const fileConfig of files) {
    if (
      !fileConfig.previousUpload &&
      !fileConfig.axonSource &&
      !validateAudioFile(fileConfig.file, fileConfig.audioType)
    ) {
      return;
    }
  }

  const abortController = new AbortController();
  let currentFileIndex = 0;
  let currentFileName = files[0]?.file.name || "";
  let currentFileType: "narrative" | "interaction" = files[0]?.audioType || "narrative";

  // Prevent accidental page refresh
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
    return "";
  };
  window.addEventListener("beforeunload", handleBeforeUnload);

  const showCancelledState = (tid: string) => {
    toast.custom(
      () =>
        React.createElement(UploadProgressToast, {
          progress: 0,
          fileName: currentFileName,
          onCancel: () => {},
          cancelled: true,
          fileType: currentFileType,
          currentFileIndex: currentFileIndex + 1,
          totalFiles: files.length,
        }),
      { id: tid, duration: 2000 }
    );
  };

  const updateToast = (
    tid: string,
    progress: number,
    remainingSeconds?: number,
    fileIndex?: number,
    fileType?: "narrative" | "interaction",
    fileName?: string
  ) => {
    if (fileIndex !== undefined) currentFileIndex = fileIndex;
    if (fileType) currentFileType = fileType;
    if (fileName) currentFileName = fileName;

    toast.custom(
      () =>
        React.createElement(UploadProgressToast, {
          progress,
          fileName: currentFileName,
          onCancel: () => {
            abortController.abort();
            showCancelledState(tid);
          },
          remainingSeconds,
          fileType: currentFileType,
          currentFileIndex: currentFileIndex + 1,
          totalFiles: files.length,
        }),
      { id: tid, duration: Infinity }
    );
  };

  // Create initial toast
  const toastId = toast.custom(
    (t) =>
      React.createElement(UploadProgressToast, {
        progress: 0,
        fileName: files[0]?.file.name,
        onCancel: () => {
          abortController.abort();
          showCancelledState(t.id);
        },
        fileType: files[0]?.audioType,
        currentFileIndex: 1,
        totalFiles: files.length,
      }),
    { duration: Infinity }
  );

  let submissionId: string | undefined;
  // Adding to a form: started on the server, so a failure must end it again.
  let supplementStarted = false;
  let failedCount = 0;

  try {
    // STEP 1: Create submission (or open a new round on an existing one)
    if (adding) {
      await axios.post(`/api/forms/submissions/${config.submissionId}/supplement`);
      supplementStarted = true;
      submissionId = config.submissionId;
    } else {
      const submissionResponse = await axios.post("/api/forms/submissions", {
        template_id: templateId,
        input_source: "audio",
        status: "draft",
      });
      submissionId = submissionResponse.data.id;
    }

    if (!submissionId) {
      throw new Error("Failed to create form submission");
    }

    onSubmissionCreated?.(submissionId);

    // Build shared metadata for a file
    const buildBaseMetadata = (fileConfig: FileConfig): Record<string, any> => ({
      audioType: fileConfig.audioType,
      context: fileConfig.additionalContext,
      ...(fileConfig.audioType === "interaction" &&
        fileConfig.speakers.length > 0 && {
          speakers: fileConfig.speakers.map((s) => ({
            name: s.name,
            role: s.role || "",
          })),
          speakerMap: fileConfig.speakers.reduce((acc, speaker, idx) => {
            acc[`spk_${idx}`] = {
              name: speaker.name,
              initials: speaker.name
                .split(" ")
                .map((n) => n[0])
                .join(""),
            };
            return acc;
          }, {} as Record<string, { name: string; initials: string }>),
        }),
    });

    // STEP 2: Create all file rows up front so they exist in "pending" state.
    // Without this, processing a previous-upload file (which finishes synchronously
    // via /completed) would trigger the all-files-done check before later rows
    // exist, prematurely invoking the PDF Filler Lambda.
    const fileIds: (string | undefined)[] = new Array(files.length).fill(undefined);

    for (let i = 0; i < files.length; i++) {
      const fileConfig = files[i];
      const file = fileConfig.file;
      const prev = fileConfig.previousUpload;
      const axon = fileConfig.axonSource;
      const baseMetadata = buildBaseMetadata(fileConfig);

      let entryBody: Record<string, any>;
      if (prev) {
        entryBody = {
          file_url: prev.fileUrl,
          file_name: prev.fileName,
          file_size: prev.fileSize,
          file_type: file.type || "audio/mp4",
          media_type: "audio",
          metadata: {
            ...baseMetadata,
            source: "previous_upload",
            sourceCaseFileId: prev.caseFileId,
            originalAudioType: prev.originalAudioType,
            transcriptionUrl: prev.transcriptionUrl,
          },
          order: i,
        };
      } else if (axon) {
        // Axon body cam files always go through Amazon Transcribe (interaction)
        entryBody = {
          file_url: "",
          file_name: axon.title || "evidence",
          file_size: `${(axon.size / 1024 / 1024).toFixed(2)} MB`,
          file_type: axon.contentType || "video/mp4",
          media_type: "audio",
          metadata: {
            ...baseMetadata,
            audioType: "interaction",
            source: "axon",
            evidenceId: axon.evidenceId,
          },
          order: i,
        };
      } else {
        entryBody = {
          file_url: "",
          file_name: file.name,
          file_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          file_type: file.type || `audio/${getFileExtension(file)}`,
          media_type: "audio",
          metadata: baseMetadata,
          order: i,
        };
      }

      try {
        const fileEntryResponse = await axios.post(
          `/api/forms/submissions/${submissionId}/files`,
          entryBody
        );
        fileIds[i] = fileEntryResponse.data.file.id;
      } catch (createError) {
        const displayName = prev?.fileName || axon?.title || file.name;
        console.error(`Failed to create file entry for ${displayName}:`, createError);
        toast.error(`Failed to register ${displayName}`, { duration: 3000 });
      }
    }

    // STEP 3: Process each file
    for (let i = 0; i < files.length; i++) {
      const fileConfig = files[i];
      const file = fileConfig.file;
      const prev = fileConfig.previousUpload;
      const axon = fileConfig.axonSource;
      const fileId = fileIds[i];

      const displayName = prev?.fileName || axon?.title || file.name;
      const effectiveAudioType: "narrative" | "interaction" = axon
        ? "interaction"
        : fileConfig.audioType;
      updateToast(toastId, 0, undefined, i, effectiveAudioType, displayName);

      if (!fileId) {
        // Row creation failed in step 2 — already toasted, skip processing
        failedCount++;
        continue;
      }

      try {
        if (prev) {
          // REUSE PATH: previous upload — skip S3 upload and reuse the existing
          // transcription. The /completed endpoint aggregates transcriptions
          // and invokes the PDF Filler Lambda once all files are done.
          if (!prev.transcriptionUrl) {
            throw new Error(
              `${prev.fileName} has no transcription yet. Please wait for it to finish processing and try again.`
            );
          }

          updateToast(toastId, 100, undefined, i, fileConfig.audioType, displayName);

          await axios.post(
            `/api/forms/submissions/${submissionId}/files/${fileId}/completed`,
            { transcriptionUrl: prev.transcriptionUrl }
          );
        } else if (axon) {
          // AXON PATH: server streams Axon → S3 → invokes Transcribe Lambda.
          // The transfer is async — we fire and forget so the user can leave.
          axios
            .post(
              `/api/evidence/${axon.evidenceId}/transfer`,
              {
                title: axon.title,
                submissionId,
                submissionFileId: fileId,
              },
              { signal: abortController.signal }
            )
            .catch((err) => {
              if (axios.isAxiosError(err) && err.code === "ERR_CANCELED") return;
              console.error(`Axon transfer failed for ${axon.title}:`, err);
              axios
                .patch(`/api/forms/submissions/files/${fileId}/status`, {
                  processing_status: "failed",
                  error_message:
                    err instanceof Error ? err.message : String(err),
                })
                .catch(() => {});
            });
        } else {
          // Get upload URL
          const urlResponse = await axios.post("/api/get-upload-url", {
            userId: user?.id,
            fileName: file.name,
            audioType: fileConfig.audioType,
            submissionId,
            submissionFileId: fileId,
            userName: user?.name,
            badgeId: user?.badgeID,
            localDateTimeOffset: new Date()
              .toLocaleString()
              .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
          });
          const uploadData = urlResponse.data;

          // Upload to S3
          await uploadAudioToS3(
            file,
            uploadData.uploadURL,
            (progress, remainingSeconds) => {
              updateToast(toastId, progress, remainingSeconds, i, fileConfig.audioType, file.name);
              onProgress?.(progress);
            },
            abortController.signal
          );

          // Update file entry with S3 URL
          await axios.patch(`/api/forms/submissions/files/${fileId}/metadata`, {
            file_url: generateS3AudioUrl(uploadData.key, fileConfig.audioType),
          });

          // Trigger processing
          if (fileConfig.audioType === "narrative") {
            await axios.post("/api/transcribe", {
              key: uploadData.key,
              submissionId,
              submissionFileId: fileId,
              localDateTimeOffset: new Date()
                .toLocaleString()
                .replace(/[^a-zA-Z0-9\s+\-=._:/@]/g, ""),
            });
          } else if (fileConfig.audioType === "interaction") {
            await axios.post(
              `/api/forms/submissions/files/${fileId}/trigger-processing`
            );
          }
        }
      } catch (fileError) {
        if (axios.isAxiosError(fileError) && fileError.code === "ERR_CANCELED") {
          throw fileError;
        }

        console.error(`Error uploading file ${displayName}:`, fileError);
        failedCount++;

        // Mark file as failed
        try {
          await axios.patch(`/api/forms/submissions/files/${fileId}/status`, {
            processing_status: "failed",
            error_message:
              fileError instanceof Error ? fileError.message : String(fileError),
          });
        } catch (statusErr) {
          console.error("Failed to update file status:", statusErr);
        }

        toast.error(`Failed to upload ${displayName}, continuing with others...`, {
          duration: 3000,
        });
      }
    }

    // Remove beforeunload listener
    window.removeEventListener("beforeunload", handleBeforeUnload);

    // Adding to a form and nothing got through: nothing will ever finish this
    // round, so put the form back as it was.
    if (adding && failedCount === files.length) {
      await axios.post(`/api/forms/submissions/${submissionId}/supplement/cancel`).catch(() => {});
      const msg = files.length === 1 ? "The recording could not be uploaded" : "None of the recordings could be uploaded";
      toast.error(msg, { id: toastId });
      onError?.(msg);
      return;
    }

    // All files uploaded
    const uploadMessage = adding
      ? "Uploaded. Adding to the form..."
      : files.length > 1
        ? `All files uploaded. Filling form with ${files.length} files...`
        : "File uploaded. Filling form...";
    toast.loading(uploadMessage, { id: toastId, duration: Infinity });

    // Start polling
    pollFormSubmissionStatus(submissionId, {
      onSuccess: (data) => {
        onSuccess?.({
          submissionId,
          ...data,
        });
      },
      onError: (error) => {
        console.error("Form fill processing failed:", error);
        onError?.(error);
      },
      toastId,
      supplement: adding,
    });
  } catch (error) {
    window.removeEventListener("beforeunload", handleBeforeUnload);

    if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
      console.log("Form fill upload cancelled by user");
      showCancelledState(toastId);

      // Adding to a form: put it back as it was. Otherwise delete the new submission.
      if (supplementStarted) {
        await axios.post(`/api/forms/submissions/${submissionId}/supplement/cancel`).catch(() => {});
      } else if (submissionId) {
        try {
          await axios.delete(`/api/forms/submissions/${submissionId}`);
        } catch (deleteError) {
          console.error("Failed to delete submission after cancellation:", deleteError);
        }
      }
      return;
    }

    console.error("Error in form fill upload:", error);
    logAudioError(
      error,
      "FormAudioProcessingService",
      "processFormFill",
      files[0]?.file || new Blob(),
      { fileCount: files.length }
    );

    if (supplementStarted) {
      await axios.post(`/api/forms/submissions/${submissionId}/supplement/cancel`).catch(() => {});
    }
    // The server's reason (e.g. "still being filled") reads better than a generic upload error.
    const serverMessage = axios.isAxiosError(error) ? (error.response?.data as any)?.error : undefined;
    const errorInfo = getAudioErrorInfo(error);
    const message = adding && serverMessage ? serverMessage : errorInfo.errorMessage;
    toast.error(message, { id: toastId });
    onError?.(message);
  }
};

// The submissions lists listen for this so a fill that starts or finishes shows up
// at once, instead of waiting for their next poll.
export const SUBMISSIONS_CHANGED_EVENT = "pnai:submissions-changed";
const announceSubmissionsChanged = () => window.dispatchEvent(new Event(SUBMISSIONS_CHANGED_EVENT));

// ---- Filling from what is already in the app ----------------------------------
// A case, or chosen Files. The server reads the text itself, so there is nothing
// to upload: create the submission, then follow it exactly like the audio flow
// does (same polling, same onSubmissionCreated / onSuccess / onError).

async function startSourceFill(
  path: string,
  body: Record<string, unknown>,
  config: FormFillConfig,
  startMessage: string,
) {
  const toastId = `form-fill-${Date.now()}`;
  // Clicking the in-progress toast goes where the caller says (see toastActions).
  if (config.onViewProgress) setToastAction(toastId, config.onViewProgress);
  toast.loading(startMessage, { id: toastId, duration: Infinity });
  try {
    const { data } = await axios.post(path, body);
    const submissionId: string = data.submissionId;
    config.onSubmissionCreated?.(submissionId);
    announceSubmissionsChanged();

    // Say when part of the source could not be used, instead of a quietly thinner fill.
    if (data.skipped?.length) {
      toast(`Skipped ${data.skipped.length} recording${data.skipped.length === 1 ? "" : "s"} that could not be read: ${data.skipped.join(", ")}`);
    }
    if (data.truncated) {
      toast("The source was very long, so only the first part was read.");
    }

    pollFormSubmissionStatus(submissionId, {
      onSuccess: (result) => {
        clearToastAction(toastId);
        announceSubmissionsChanged();
        config.onSuccess?.({ submissionId, ...result });
      },
      onError: (error) => {
        console.error("Form fill processing failed:", error);
        clearToastAction(toastId);
        announceSubmissionsChanged();
        config.onError?.(error);
      },
      toastId,
    });
  } catch (error: any) {
    const message = error?.response?.data?.error || "Could not start filling the form";
    clearToastAction(toastId);
    toast.error(message, { id: toastId });
    config.onError?.(message);
  }
}

/** Fill a form from a case: its narrative plus every recording's transcript. */
export const fillFormFromCase = (caseId: string, config: FormFillConfig) =>
  startSourceFill("/api/forms/submissions/from-case", { template_id: config.templateId, case_id: caseId }, config, "Reading the case and filling the form...");

/** Fill a form from chosen Files-library files. */
export const fillFormFromFiles = (files: { source: string; id: string }[], config: FormFillConfig) =>
  startSourceFill(
    "/api/forms/submissions/from-files",
    { template_id: config.templateId, files },
    config,
    files.length === 1 ? "Reading the file and filling the form..." : `Reading ${files.length} files and filling the form...`,
  );

// ---- Several forms from one case ------------------------------------------------
// One submission per form, each read from the same case. The forms are started
// one after another; if the CASE is the problem (a recording still transcribing,
// no access) every form would fail the same way, so the rest are not tried and
// the officer sees that one reason, not one error per form.

export interface CaseFillResult {
  templateId: string;
  submissionId?: string;
  error?: string;
  skipped?: string[];
  truncated?: boolean;
}

const waitForFill = (submissionId: string) =>
  new Promise<{ ok: boolean; error?: string }>((resolve) => {
    pollFormSubmissionStatus(submissionId, {
      onSuccess: () => resolve({ ok: true }),
      onError: (error) => resolve({ ok: false, error }),
    });
  });

// A 409 about the FORM ("still being processed") is that form's own problem.
const isCaseProblem = (status: number | undefined, message: string) =>
  status === 403 || status === 404 || (status === 409 && !/This form is still being processed/i.test(message));

export async function fillFormsFromCase(
  caseId: string,
  templateIds: string[],
  opts: {
    onViewProgress?: () => void;
    onSubmissionCreated?: (submissionId: string) => void;
    // Called once, when every started fill has finished (or failed to start).
    onFinished: (results: CaseFillResult[]) => void;
  },
): Promise<void> {
  const toastId = `form-fill-${Date.now()}`;
  const total = templateIds.length;
  const progressText = (done: number) =>
    total === 1
      ? "Reading the case and filling the form..."
      : done === 0
        ? `Reading the case and filling ${total} forms...`
        : `Filling ${total} forms from the case... ${done} of ${total} done`;
  // Clicking the toast goes to the forms page (see toastActions); the toast stays up.
  if (opts.onViewProgress) setToastAction(toastId, opts.onViewProgress);
  toast.loading(progressText(0), { id: toastId, duration: Infinity });

  const results: CaseFillResult[] = [];
  let blocked: string | undefined;
  for (const templateId of templateIds) {
    if (blocked) {
      results.push({ templateId, error: blocked });
      continue;
    }
    try {
      const { data } = await axios.post("/api/forms/submissions/from-case", { template_id: templateId, case_id: caseId });
      opts.onSubmissionCreated?.(data.submissionId);
      announceSubmissionsChanged();
      results.push({ templateId, submissionId: data.submissionId, skipped: data.skipped, truncated: data.truncated });
    } catch (error: any) {
      const message = error?.response?.data?.error || "Could not start filling the form";
      results.push({ templateId, error: message });
      if (isCaseProblem(error?.response?.status, message)) blocked = message;
    }
  }

  // The summary comes only once EVERY form has finished. Meanwhile the toast
  // counts them off, and the lists refresh as each one lands.
  let done = results.filter((r) => !r.submissionId).length;
  await Promise.all(
    results
      .filter((r) => r.submissionId)
      .map(async (r) => {
        const outcome = await waitForFill(r.submissionId!);
        if (!outcome.ok) r.error = outcome.error || "Form filling failed";
        done += 1;
        announceSubmissionsChanged();
        if (done < total) toast.loading(progressText(done), { id: toastId, duration: Infinity });
      }),
  );

  clearToastAction(toastId);
  toast.dismiss(toastId);
  opts.onFinished(results);
}

/**
 * Follow an addition to a filled form that is already running (the page was
 * opened or reloaded while it filled). No toast: the page shows its own state.
 */
export const followFormSupplement = (
  submissionId: string,
  callbacks: { onSuccess?: (data: any) => void; onError?: (error: string) => void }
) => pollFormSubmissionStatus(submissionId, { ...callbacks, supplement: true });
